import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/** Pulls a readable title/message/stack out of whatever react-router's data router caught - a thrown Response, an Error, or an arbitrary value. */
function describeError(error: unknown, t: (key: string) => string) {
  if (isRouteErrorResponse(error)) {
    return {
      title: `${error.status} ${error.statusText}`,
      message:
        typeof error.data === "string" ? error.data : JSON.stringify(error.data),
      stack: undefined,
    };
  }

  if (error instanceof Error) {
    return {
      title: error.name || t("errorPage.applicationError"),
      message: error.message,
      stack: error.stack,
    };
  }

  return {
    title: t("errorPage.unknownError"),
    message: typeof error === "string" ? error : JSON.stringify(error),
    stack: undefined,
  };
}

/** Full-screen fallback rendered by every route's errorElement when a page throws during render/loading. */
export function ErrorPage() {
  const { t } = useTranslation("layout");
  const error = useRouteError();
  const { title, message, stack } = describeError(error, t);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4 dark:bg-[#0f1114]">
      <Card className="w-full max-w-2xl border-red-200/80 dark:border-red-900/60">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex size-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-950">
            <AlertTriangle className="size-7 text-red-600 dark:text-red-400" />
          </div>
          <CardTitle className="text-xl">{t("errorPage.heading")}</CardTitle>
          <CardDescription>{title}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {message && (
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap wrap-break-word rounded-md border border-slate-200 bg-slate-100 p-3 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
              {message}
            </pre>
          )}
          {stack && (
            <details className="text-xs text-slate-500 dark:text-slate-400">
              <summary className="cursor-pointer select-none font-medium">
                {t("errorPage.stackTrace")}
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap wrap-break-word rounded-md border border-slate-200 bg-slate-100 p-3 dark:border-slate-800 dark:bg-slate-900">
                {stack}
              </pre>
            </details>
          )}
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RefreshCw />
              {t("errorPage.reloadPage")}
            </Button>
            <Button onClick={() => (window.location.href = "/")}>
              <Home />
              {t("errorPage.goToDashboard")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
